'use client';

import { useEffect, useRef, useState } from 'react';
import type { PortableTextBlock } from '@portabletext/types';
import { PortableTextRenderer } from '@/components/content';
import { IconCheck, IconCross, IconXMark } from '@/components/icons';
import { cn } from '@/lib/utils/tw-merge';

/** Success/error both auto-dismiss after this delay; the progress bar fills over
 *  the same duration so both stay in sync from a single source of truth. */
export const FORM_FEEDBACK_AUTO_DISMISS_MS = 3000;

type FormFeedbackVariant = 'success' | 'error';

/** Result texts used when the block supplies no CMS-authored copy. A block that
 *  does (successText/errorText of formBlock and the tabFormBlock tabs) passes
 *  `content` instead. */
const DEFAULT_TEXT: Record<FormFeedbackVariant, { title: string; body: string }> = {
    success: { title: 'Ihre Anfrage ist eingegangen.', body: 'Wir melden uns innerhalb eines Werktags.' },
    error: { title: 'Es ist ein Fehler aufgetreten.', body: 'Bitte versuchen Sie es erneut.' },
};

/** Result panel that replaces the form after submit, laid out like the design's
 *  confirmation: status icon, heading, text and one button. When autoDismiss is
 *  set, a progress bar fills over FORM_FEEDBACK_AUTO_DISMISS_MS and then
 *  triggers onDismiss. */
export function FormFeedback({
    variant,
    buttonLabel,
    onDismiss,
    autoDismiss = false,
    closes = false,
    content,
}: {
    variant: FormFeedbackVariant;
    buttonLabel: string;
    onDismiss: () => void;
    autoDismiss?: boolean;
    /** The button closes the dialog — it carries the close icon. */
    closes?: boolean;
    content?: PortableTextBlock[];
}) {
    const [filled, setFilled] = useState(false);

    // Bind the timer to autoDismiss only; read the latest onDismiss through a ref
    // so a changed callback identity can't restart the countdown mid-fill.
    const onDismissRef = useRef(onDismiss);
    useEffect(() => {
        onDismissRef.current = onDismiss;
    });

    useEffect(() => {
        if (!autoDismiss) return;
        const raf = requestAnimationFrame(() => setFilled(true));
        const timer = setTimeout(() => onDismissRef.current(), FORM_FEEDBACK_AUTO_DISMISS_MS);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(timer);
        };
    }, [autoDismiss]);

    const { title, body } = DEFAULT_TEXT[variant];

    return (
        <div
            className={cn('form-feedback', `form-feedback--${variant}`)}
            role={variant === 'error' ? 'alert' : 'status'}
        >
            <div className={cn('form-feedback-icon', `form-feedback-icon-${variant}`)}>
                {variant === 'success' ? <IconCheck className="size-4" /> : <IconCross className="size-4" />}
            </div>

            <div className="form-feedback-text">
                {content?.length ? (
                    <PortableTextRenderer value={content} />
                ) : (
                    <>
                        <p className="form-feedback-title">{title}</p>
                        <p>{body}</p>
                    </>
                )}
            </div>

            <button type="button" className="btn btn-primary mt-6.5 w-full sm:w-auto" onClick={onDismiss}>
                {buttonLabel}
                {closes ? <IconXMark className="size-4" aria-hidden="true" /> : null}
            </button>

            {autoDismiss && (
                <div className="form-feedback-progress" aria-hidden="true">
                    <div
                        className="form-feedback-progress-fill"
                        style={{
                            width: filled ? '100%' : '0%',
                            transitionDuration: `${FORM_FEEDBACK_AUTO_DISMISS_MS}ms`,
                        }}
                    />
                </div>
            )}
        </div>
    );
}
